import { z } from 'zod';

/**
 * When a report is due. One function, used by the order form for its estimate and by the worker when an order is
 * placed, so the two cannot drift. Everything is reckoned in Toronto time through `Intl` (no date library):
 * a business day is a weekday that is not a holiday; day one is the placement day when it is a business day and
 * the order is placed before the cut-off, otherwise the next business day; the report is due at the delivery time
 * on business day number `turnaroundDays`.
 */
export const BUSINESS_TIME_ZONE = 'America/Toronto';
/** The cut-off the form states: noon in Toronto. */
export const CUTOFF_HOUR = 12;
/** Placement is accepted this many minutes past the stated cut-off, so paying seconds after noon still counts. */
export const CUTOFF_GRACE_MINUTES = 5;
/** Reports are delivered by 17:30 in Toronto. */
export const DELIVERY_HOUR = 17;
export const DELIVERY_MINUTE = 30;

/** A calendar day in Toronto, `YYYY-MM-DD`: the form `business_holidays.day` comes in. */
export type LocalDay = string;

/** A row of `public.business_holidays`: a holiday on the weekday it is observed. */
export const businessHolidaySchema = z.object({ day: z.iso.date(), name: z.string().min(1) });
export type BusinessHoliday = z.infer<typeof businessHolidaySchema>;

const torontoParts = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

type Wall = { day: LocalDay; minutes: number; asUtc: number };

/** The Toronto wall clock at an instant: the day, the minutes since midnight, and the wall time read as if UTC. */
function wallClock(at: Date): Wall {
  const p = Object.fromEntries(torontoParts.formatToParts(at).map((x) => [x.type, x.value]));
  const [y, m, d, h, min, s] = [p.year, p.month, p.day, p.hour, p.minute, p.second].map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  return {
    day: `${p.year}-${p.month}-${p.day}`,
    minutes: h * 60 + min,
    asUtc: Date.UTC(y, m - 1, d, h, min, s),
  };
}

/** The instant at which Toronto's wall clock reads `hour:minute` on `day`. */
function torontoInstant(day: LocalDay, hour: number, minute: number): Date {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const wanted = Date.UTC(y, m - 1, d, hour, minute);
  // Start from the wall time read as UTC and correct by the zone's offset there; twice, in case the first guess
  // sits on the other side of a clock change.
  let guess = wanted;
  for (let i = 0; i < 2; i++) guess += wanted - wallClock(new Date(guess)).asUtc;
  return new Date(guess);
}

function nextDay(day: LocalDay): LocalDay {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** A weekday that is not a holiday. */
export function isBusinessDay(day: LocalDay, holidays: readonly LocalDay[]): boolean {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return weekday !== 0 && weekday !== 6 && !holidays.includes(day);
}

function nextBusinessDay(day: LocalDay, holidays: readonly LocalDay[]): LocalDay {
  let next = nextDay(day);
  while (!isBusinessDay(next, holidays)) next = nextDay(next);
  return next;
}

/** Business day one of an order placed at `placedAt`, given how many minutes past midnight still count as today. */
function dayOne(placedAt: Date, holidays: readonly LocalDay[], lastMinute: number): LocalDay {
  const { day, minutes } = wallClock(placedAt);
  return isBusinessDay(day, holidays) && minutes < lastMinute
    ? day
    : nextBusinessDay(day, holidays);
}

/**
 * When an order placed at `placedAt` is due: 17:30 Toronto on business day number `turnaroundDays`, counting the
 * placement day as day one when it is a business day and the Toronto time is before 12:05.
 */
export function computeDueAt({
  placedAt,
  turnaroundDays,
  holidays,
}: {
  placedAt: Date;
  turnaroundDays: number;
  holidays: readonly LocalDay[];
}): Date {
  if (!Number.isInteger(turnaroundDays) || turnaroundDays < 1)
    throw new RangeError('turnaroundDays must be a positive whole number');
  let day = dayOne(placedAt, holidays, CUTOFF_HOUR * 60 + CUTOFF_GRACE_MINUTES);
  for (let n = 1; n < turnaroundDays; n++) day = nextBusinessDay(day, holidays);
  return torontoInstant(day, DELIVERY_HOUR, DELIVERY_MINUTE);
}

/**
 * The next cut-off still ahead of `now`: noon Toronto today when today is a business day and it is not noon yet,
 * otherwise noon on the next business day. The form shows it in the customer's own time zone, and estimates the
 * delivery as `computeDueAt({ placedAt: cutoffFor(now, holidays), … })`.
 */
export function cutoffFor(now: Date, holidays: readonly LocalDay[]): Date {
  return torontoInstant(dayOne(now, holidays, CUTOFF_HOUR * 60), CUTOFF_HOUR, 0);
}
